import { setUserId as amplitudeSetUserId } from '@amplitude/analytics-browser';
import { useQuery } from '@tanstack/react-query';
import { QueryKey, UserStatus } from 'common-types';
import { useEffect, useMemo } from 'react';
import { useAnalytics } from 'services/analytics';
import { api } from 'services/api';
import { useLocalStorageStore, useStore } from 'store';
import { CHEAP_GUIDE, TRIAL_GUIDE } from 'utils';
import { EU_TAX_COUNTRIES } from 'utils/countries';
import { useShallow } from 'zustand/shallow';

import { IUseGetUserData } from './utils/types';

export const useGetUserData = (): IUseGetUserData => {
  const { trackEvent } = useAnalytics();

  // Session initialization is now handled in TanStack Router's beforeLoad (_authenticated.tsx)
  // By the time authenticated components render, session is guaranteed to be initialized
  const { isSessionInitialized, setIsUserWithVat } = useStore(
    useShallow((state) => ({
      isSessionInitialized: state.isSessionInitialized,
      setIsUserWithVat: state.setIsUserWithVat,
    })),
  );

  const { setUserId, setDomain } = useLocalStorageStore(
    useShallow((state) => ({
      setUserId: state.setUserId,
      setDomain: state.setDomain,
    })),
  );

  const {
    data: userData,
    isError,
    isLoading,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.USER_DATA],
    queryFn: () => api.getUserInfo(),
    enabled: isSessionInitialized,
  });

  useEffect(() => {
    if (userData?.id) {
      setUserId(userData.id);
      amplitudeSetUserId(userData.id);
    }

    if (userData?.domain) {
      setDomain(userData.domain);
    }

    if (userData?.country && EU_TAX_COUNTRIES.includes(userData?.country)) {
      setIsUserWithVat(true);
    }
  }, [userData, setUserId, setDomain, setIsUserWithVat]);

  const pureGuides = useMemo(() => {
    // it's a temporary fix to remove trial and cheap guides from the list
    return Array.from(new Set(userData?.guides?.filter((guide) => guide !== TRIAL_GUIDE && guide !== CHEAP_GUIDE)));
  }, [userData?.guides]);

  const isSubscriptionExpired = userData?.status === UserStatus.CANCELED || userData?.status === UserStatus.DEFAULT;

  useEffect(() => {
    if (isSuccess) {
      // @ts-ignore
      window.zE('messenger', 'loginUser', function (callback: any) {
        try {
          callback(userData?.zendeskJwtToken);
        } catch (e) {
          trackEvent('chat_userId_transfer_error', {
            error: JSON.stringify(e),
          });
        }
      });
    }
  }, [isSuccess, trackEvent, userData?.zendeskJwtToken]);

  return {
    userData,
    isError,
    isLoading,
    isSuccess,
    userId: userData?.id,
    funnelId: userData?.funnelId,
    profileName: userData?.profileName,
    userNotes: userData?.notes || [],
    userProgress: userData?.userProgress,
    userSelectedMood: userData?.selectedMood,
    userRecent: userData?.recent || [],
    userName: userData?.userName,
    userEmail: userData?.email,
    userPhone: userData?.phone,
    userSubscriptionId: userData?.subscriptionId,
    userSchema: userData?.schema,
    userStatus: userData?.status,
    userGoal: userData?.userGoal || '',
    userUltimateWhy: userData?.userUltimateWhy || '',
    userPhoto: userData?.photo,
    userGuides: userData?.guides,
    userPureGuides: pureGuides,
    userDomain: userData?.domain,
    userCountry: userData?.country,
    userDevice: userData?.deviceType,
    isLifetimePremium: userData?.isLifetimePremium,
    isUpsellFinished: userData?.isUpsellFinished,
    isSubscriptionExpired,
    isCompleteQuiz: userData?.completeQuiz,
    isWebOnboardingDone: userData?.isWebOnboardingDone,
    isMobile: userData?.isMobile,
    refetch,
  };
};
